/**
 * Equipment cost overrides: what the user typed instead of the catalog.
 *
 * The CAPEX hardware term is normally catalog unit price x installed count.
 * That is the right default and the figure a reviewer can re-derive from the
 * catalog page. An override exists for the prices a catalog cannot express -- a
 * negotiated discount, a quoted turn-key package, hardware bought before the
 * study -- because the alternative is that somebody edits the shared catalog
 * entry for every project, or fudges the EUR/Wdc installation fields until the
 * total happens to look right.
 *
 * An override replaces the component's HARDWARE VALUE, which feeds two things:
 * the upfront CAPEX outflow and the base for the percentage O&M and insurance
 * rates. The backend applies it to both from one place (pv_hardware_value /
 * inverter_hardware_value), so a study cannot end up booking 150k of modules
 * and insuring 190k of them.
 *
 * Each override carries the name of the catalog model it was entered for. Swap
 * the equipment and the price describes nothing, so it is dropped rather than
 * spent on hardware the study no longer contains. The backend repeats that
 * check, because a config can also arrive from a saved project or an API
 * client that never ran this code.
 */

export const DEFAULT_EQUIPMENT_COST_OVERRIDE = {
  modules: "",
  inverters: "",
  battery: "",
  moduleName: "",
  inverterName: "",
  batteryName: "",
};

/** The components an override can apply to, and where each stores its stamp. */
export const EQUIPMENT_OVERRIDE_COMPONENTS = [
  { valueKey: "modules", nameKey: "moduleName", label: "PV modules" },
  { valueKey: "inverters", nameKey: "inverterName", label: "inverters" },
  { valueKey: "battery", nameKey: "batteryName", label: "battery system" },
];

/**
 * An entered equipment cost, or null when the catalog should decide.
 *
 * Blank is not zero. Zero is a real answer -- donated hardware, a component
 * somebody else is paying for -- and a study that could not tell it from "I
 * have not typed anything" would price that hardware at list without saying so.
 *
 * Negatives are refused rather than clamped: a negative equipment cost is a
 * typo every time, and clamping it to zero would hide the typo behind a
 * plausible number.
 */
export function parseOverrideEur(raw) {
  const text = String(raw ?? "").trim();
  if (text === "") return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

/**
 * Which overrides no longer describe the equipment in the study.
 *
 * Returns { next, dropped }: the override object with the stale entries
 * cleared, and a human-readable list of what went and why. An override with no
 * stamp is kept -- it predates the stamp, or came from a client that does not
 * send one, and discarding it would silently re-price a study that was correct
 * when it was saved. An unknown CURRENT name is also treated as a match: a page
 * that has not finished loading its catalog is not evidence against the user.
 */
export function pruneStaleOverrides(override, currentNames) {
  const source = { ...DEFAULT_EQUIPMENT_COST_OVERRIDE, ...(override || {}) };
  const next = { ...source };
  const dropped = [];
  EQUIPMENT_OVERRIDE_COMPONENTS.forEach(({ valueKey, nameKey, label }) => {
    if (String(source[valueKey] ?? "").trim() === "") return;
    const stamped = String(source[nameKey] ?? "").trim();
    const current = String((currentNames || {})[valueKey] ?? "").trim();
    if (!stamped || !current || stamped === current) return;
    next[valueKey] = "";
    next[nameKey] = "";
    dropped.push(`${label} (was entered for ${stamped})`);
  });
  return { next, dropped };
}
